import type { ConditionModel } from '../../../../typings'
import { useMemo } from 'react'
import { useFormikContext } from 'formik'
import { useGetOptions } from '@user/components/Settings/LeadAssignmentRule/hooks'
import { useTranslation } from 'react-i18next'

export function useData() {
  const { t } = useTranslation()

  const {
    values: { attribute, assignmentRuleConditionValues },
    touched,
    errors,
  } = useFormikContext<ConditionModel>()
  const getOptions = useGetOptions()

  const label = useMemo(() => {
    switch (attribute) {
      case 'ZipCode':
        return `${t('postalCode')} *`
      case 'Campaign':
        return 'Campaign *'
      case 'LeadSource':
        return 'Lead Source *'
      case 'State':
        return `${t('stateAssignmentRuleLabel')} *`
      case 'LocationType':
        return 'Location Type *'
      case 'Division':
        return 'Division *'
      case 'CreatedBy':
        return 'Created By *'
      case 'TargetPest':
        return 'Target Pests *'
      case 'LeadType':
        return 'Type *'
      case 'Territory':
        return 'Territory *'
      default:
        return ''
    }
  }, [attribute, t])

  const placeholder = useMemo(() => {
    switch (attribute) {
      case 'ZipCode':
        return `${t('postalCode')}s`
      case 'Campaign':
        return 'Campaigns'
      case 'LeadSource':
        return 'Lead Sources'
      case 'State':
        return `${t('state')}s`
      case 'LocationType':
        return 'Location Types'
      case 'Division':
        return 'Divisions'
      case 'CreatedBy':
        return 'Users'
      case 'TargetPest':
        return 'Target Pests'
      case 'LeadType':
        return 'Type'
      case 'Territory':
        return 'Territories'
      default:
        return ''
    }
  }, [t, attribute])

  const defaultOptions = getOptions(attribute)

  const disabledOptions = useMemo(() => {
    if (attribute == 'State') {
      return assignmentRuleConditionValues
        ?.filter((value: any) => {
          return !defaultOptions?.some(
            (option: any) => option?.id === value?.id,
          )
        })
        ?.map((row) => {
          return { ...row, disabled: true }
        })
    }
    return []
  }, [defaultOptions, attribute, assignmentRuleConditionValues])

  const options = useMemo(() => {
    if (attribute == 'State') {
      return [...disabledOptions, ...defaultOptions]
    }
    return defaultOptions
  }, [defaultOptions, disabledOptions, attribute])

  const errorText = useMemo(() => {
    if (
      attribute &&
      !!touched.assignmentRuleConditionValues &&
      (!assignmentRuleConditionValues || !assignmentRuleConditionValues.length)
    ) {
      return errors.assignmentRuleConditionValues as string
    }

    return ''
  }, [
    errors.assignmentRuleConditionValues,
    touched.assignmentRuleConditionValues,
    assignmentRuleConditionValues,
    attribute,
  ])

  return {
    options,
    label,
    value: assignmentRuleConditionValues,
    attribute: attribute,
    errorText,
    placeholder,
  }
}
